import Hook from '@@types/Hook';

import { INCOMING_HOOK_LIMIT_BASIC, INCOMING_HOOK_LIMIT_LITE } from '~/constants';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { incomingHooksStore } from '~/stores/IncomingHooksStore';
import { api } from '~/stores/providers/Api2';
import { keyBy } from '~/utils/fnUtil/fnUtil';
import { runFutureFork, runFutureValue } from '~/utils/monadUtil';
import { encaseP, FutureInstance, reject, resolve, tryP } from 'fluture';
import { mergeRight, pipe } from 'ramda';

import Actions from '../Actions';

type CreateHookParams = { name: string; serviceType: string };

type DeleteHookParams = { channelId: string; hookId: string };

type UpdateHookParams = { channelId: string; hook: Hook };

class HookActions {
  constructor(private actions: Actions) {}

  private validateHookLimit = (params: CreateHookParams): FutureInstance<any, CreateHookParams> => {
    const { isBasicPlan, isBusinessPlan } = globalSettingStore;

    if (isBusinessPlan) {
      return resolve(params);
    }
    return tryP(api.hook.fetchAllHookCount).chain((count: number) => {
      const limit = isBasicPlan ? INCOMING_HOOK_LIMIT_BASIC : INCOMING_HOOK_LIMIT_LITE;
      return count >= limit ? reject(limit) : resolve(params);
    });
  };

  private openHookLimitAlert = () => {
    this.actions.modalActions.openIncomingHookLimitAlert();
  };

  handleClickHookCreate = (params: CreateHookParams) => {
    runFutureFork(
      this.openHookLimitAlert,
      pipe(encaseP(this.createHook), runFutureValue(this.actions.modalActions.openIncomingHookResultAlert)),
      this.validateHookLimit(params)
    );
  };

  createHook = (params: CreateHookParams): Promise<Hook> => {
    const channelId = mainDetailUiState.contentsChannelId;
    return api.hook.createHook(mergeRight(params, { channelIdList: [channelId] })) as Promise<Hook>;
  };

  deleteHook = async (params: DeleteHookParams): Promise<void> => {
    await api.hook.deleteHook(params.channelId, params.hookId);
    incomingHooksStore.removeHook(params.channelId, params.hookId);
  };

  fetchHooks = async (channelId: string) => {
    const hookResponse = await api.hook.fetchHooks(channelId);
    incomingHooksStore.setHookMap(channelId, keyBy('id', hookResponse.contents));
  };

  getHookUrl = (hook: Hook) => {
    const { hookDomain } = globalSettingStore.env;
    return `https://${hookDomain}/services/${hook.organizationId}/${hook.id}/${hook.token}`;
  };

  updateHook = async (params: UpdateHookParams) => {
    await api.hook.updateHook(params.hook);
    incomingHooksStore.updateHook(params.channelId, params.hook);
  };
}

export default HookActions;
